
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { SanityDocument as SanityClientDocument } from '@sanity/client';
import { Reference as SanityReference } from '@sanity/types';
import { SanityObjectSchema, ISanityObjects } from '~/types/sanity-objects';
import { SanityDocumentSchema } from '~/types/sanity-documents';

export enum CardOrientations {
  PORTRAIT = 'portrait',
  LANDSCAPE = 'landscape',
}

export type TRefDoc = SanityClientDocument<{
  genres?: SanityReference[];
  pageAvailability?: ISanityObjects[SanityObjectSchema.PAGE_AVAILABILITY];
  promoCard?: ISanityObjects[SanityObjectSchema.PROMO_CARD];
  slug?: string;
}>;

@Component({
  inheritAttrs: false,
})
export default class PromoCarouselSlide extends Vue {
  @Prop({ type: String, default: CardOrientations.LANDSCAPE })
  readonly orientation!: CardOrientations;
  @Prop(Object)
  readonly slide!: ISanityObjects[SanityObjectSchema.PROMO_CAROUSEL_ITEM];
  @Prop(Number)
  readonly slideAspectRatio?: number;
  @Prop(Number)
  readonly slideWidth?: number;

  schemas = SanityDocumentSchema;
  orientations = CardOrientations;

  @Watch('slide.documentReference._ref', { immediate: true })
  handleDocRefChange(
    current: string | undefined,
    previous: string | undefined,
  ) {
    if (current && current !== previous) {
      this.$sanityLookup.fetchReference({ _id: current });
    }
  }

  get documentRef() {
    const docRefId = this.slide?.documentReference?._ref;
    return docRefId
      ? this.$sanityLookup.getDocumentFromReference<
          SanityClientDocument<TRefDoc>
        >({ _ref: docRefId })
      : {
          isLoading: false,
          hasError: false,
        };
  }

  get isLoading() {
    return this.documentRef?.isLoading;
  }

  get defaultAspectRatio() {
    return this.orientation === this.orientations.PORTRAIT ? 9 / 16 : 16 / 9;
  }

  get defaultWidth() {
    return this.orientation === this.orientations.PORTRAIT ? 420 : 1920;
  }

  get aspectRatio() {
    return this.slideAspectRatio !== undefined
      ? this.slideAspectRatio
      : this.defaultAspectRatio;
  }

  get height() {
    return Math.round(this.width * (1 / this.aspectRatio));
  }

  get width() {
    return this.slideWidth !== undefined ? this.slideWidth : this.defaultWidth;
  }

  get ctaUrl() {
    if (this.slide?.cta?.linkToDocumentRef) {
      if (!this.documentRef?.data?.slug || !this.documentRef?.data?._type) {
        return;
      }

      if (this.documentRef?.data?.pageAvailability?.isLandingPageEnabled) {
        return this.$sanityLookup.getPageRoutePath({
          slug: this.documentRef.data?.slug,
          schema: this.documentRef.data._type as SanityDocumentSchema,
        });
      }
    } else {
      return this.slide?.cta?.url;
    }
  }

  handleClick() {
    if (!this.ctaUrl) {
      return;
    }

    if (/^https?:\/\//.test(this.ctaUrl)) {
      // Open external links in new window
      window.open(this.ctaUrl);
    } else if (/^(tel)|(mailto):/.test(this.ctaUrl)) {
      // Open tel or email links in same window
      window.location.href = this.ctaUrl;
    } else {
      // Nav without page load for relative links
      this.$router.push(this.ctaUrl);
    }
  }

  get logo() {
    return this.documentRef?.data?.promoCard?.logo;
  }

  get imageUrl() {
    if (!this.documentRef?.data?.promoCard?.heroImageLandscape) {
      return;
    }

    const img =
      this.orientation === this.orientations.PORTRAIT &&
      this.documentRef?.data?.promoCard?.heroImagePortrait
        ? this.documentRef.data.promoCard.heroImagePortrait
        : this.documentRef.data.promoCard.heroImageLandscape;

    return this.$sanityImage
      .urlFor(img)
      .width(this.width)
      .height(this.height)
      .fit('crop')
      .url();
  }

  get overline() {
    return this.documentRef?.data?.genres || [];
  }

  get text() {
    return this.documentRef?.data?.promoCard?.text;
  }
}
